import { Link } from 'react-router-dom';
import { CheckCircle, Search, Sparkles, Filter, PenLine, Rocket, Smartphone, BarChart3, ArrowRight, Zap, Globe, Lock, Clock, Users, BarChart } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const sections = [
  {
    title: 'AI Lead Discovery Pipeline',
    Icon: Search,
    IconExtra: Sparkles,
    color: 'var(--sky)',
    bg: 'var(--sky-light)',
    paragraphs: [
      'LeadNova\'s AI engine continuously scans millions of data sources to identify prospects that match your ideal customer profile. From company news and hiring signals to technology adoption and funding rounds, our algorithms surface the leads most likely to convert.',
      'Unlike traditional databases that rely on stale data, LeadNova refreshes prospect intelligence in real-time. You\'ll always have the most current contact information, company details, and intent signals at your fingertips.',
    ],
    bullets: [
      'Real-time intent signal monitoring across 50+ data sources',
      'Automatic ICP matching with 95%+ accuracy',
      'Daily delivery of pre-qualified, verified leads'
    ]
  },
  {
    title: 'Lead Database & Segmentation',
    Icon: Filter,
    color: 'var(--green)',
    bg: 'var(--green-light)',
    paragraphs: [
      'Build and manage a dynamic lead database that grows smarter over time. LeadNova enriches every contact with firmographic, technographic, and behavioral data so you can segment your audience with surgical precision.',
      'Create custom segments based on any combination of attributes—industry, company size, tech stack, funding stage, hiring velocity, and more.',
    ],
    bullets: [
      '200+ filterable attributes per contact and company',
      'Dynamic segments that auto-update with new data',
      'One-click CRM sync and CSV export'
    ]
  },
  {
    title: 'AI Outreach Assistant',
    Icon: PenLine,
    IconExtra: Sparkles,
    color: 'var(--amber)',
    bg: 'var(--amber-light)',
    paragraphs: [
      'Say goodbye to generic templates. LeadNova\'s AI Outreach Assistant generates hyper-personalized emails, LinkedIn messages, and call scripts tailored to each prospect\'s unique context.',
      'The assistant analyzes prospect data, recent activity, mutual connections, and company news to craft messages that feel genuinely personal.',
    ],
    bullets: [
      'Hyper-personalized messaging across email, LinkedIn, and phone',
      'Automatic A/B testing with AI-powered optimization',
      'Human-in-the-loop approval workflows for quality control'
    ]
  },
  {
    title: 'Campaign Engine',
    Icon: Rocket,
    color: '#7C3AED',
    bg: '#EDE9FE',
    paragraphs: [
      'Orchestrate sophisticated multi-channel campaigns that engage prospects at the right time, on the right channel, with the right message.',
      'Build visual campaign workflows with branching logic, time delays, and conditional triggers. Set up automated follow-ups that adapt based on prospect engagement.',
    ],
    bullets: [
      'Visual drag-and-drop campaign builder with branching logic',
      'Smart send-time optimization based on recipient behavior',
      'Real-time campaign analytics and performance alerts'
    ]
  },
  {
    title: 'Mobile Inbox',
    Icon: Smartphone,
    color: '#E53E6B',
    bg: '#FDE8EE',
    paragraphs: [
      'Never miss a hot lead reply again. LeadNova\'s unified mobile inbox brings all your prospect conversations into one place—email, LinkedIn, and SMS.',
      'Get push notifications for high-priority replies, approve pending messages with a swipe, and track engagement metrics on the go.',
    ],
    bullets: [
      'Unified inbox across email, LinkedIn, and SMS channels',
      'Priority notifications for high-intent prospect replies',
      'Quick-reply templates and AI-suggested responses'
    ]
  },
  {
    title: 'Analytics & Learning',
    Icon: BarChart3,
    color: 'var(--sky)',
    bg: 'var(--sky-light)',
    paragraphs: [
      'Go beyond vanity metrics. LeadNova\'s analytics dashboard gives you deep visibility into every stage of your outbound funnel—from lead discovery to closed deals.',
      'Track team performance, identify top-performing campaigns, and understand which messaging strategies drive the best results.',
    ],
    bullets: [
      'Full-funnel analytics from discovery to closed-won',
      'AI-powered recommendations to improve performance',
      'One-click executive reports and team leaderboards'
    ]
  }
];

const highlights = [
  { Icon: Zap, title: 'Lightning Fast', desc: 'Set up in minutes, not weeks. Start discovering leads on day one.' },
  { Icon: Globe, title: 'Global Coverage', desc: '210M+ contacts across 35M+ companies in 100+ countries.' },
  { Icon: Lock, title: 'Enterprise Security', desc: 'SOC 2 Type II compliant with end-to-end encryption.' },
  { Icon: Clock, title: 'Real-Time Data', desc: 'Continuously refreshed data so you always reach the right person.' },
  { Icon: Users, title: 'Team Collaboration', desc: 'Built for teams with role-based access and shared workflows.' },
  { Icon: BarChart, title: 'AI Learning', desc: 'Gets smarter with every campaign, optimizing your results over time.' },
];

export default function ProductPage() {
  const { isAuthenticated } = useAuth();
  return (
    <div className="pp">
      <section className="pp-hero">
        <div className="pp-hero-bg" />
        <div className="pp-hero-dots" />
        <div className="container pp-hero-inner">
          <div className="pp-hero-pill">
            <Sparkles size={14} />
            Platform Overview
          </div>
          <h1 className="pp-hero-title">The complete outbound platform</h1>
          <p className="pp-hero-desc">
            Six powerful modules that work together to help your team discover, engage, and convert more prospects.
          </p>
          <div className="pp-hero-cta">
            {!isAuthenticated && (
              <Link to="/pricing" className="pp-btn-primary">
                Start Free Trial <ArrowRight size={18} />
              </Link>
            )}
            <Link to="/pricing" className="pp-btn-secondary">View Pricing</Link>
          </div>
        </div>
      </section>

      <section className="pp-highlights-section">
        <div className="container">
          <div className="pp-highlights-grid">
            {highlights.map((h) => {
              const HIcon = h.Icon;
              return (
                <div key={h.title} className="pp-highlight-card">
                  <div className="pp-highlight-icon">
                    <HIcon size={20} />
                  </div>
                  <h3 className="pp-highlight-title">{h.title}</h3>
                  <p className="pp-highlight-desc">{h.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {sections.map((s, i) => {
        const SIcon = s.Icon;
        const isReversed = i % 2 === 1;
        return (
          <section key={s.title} className={`pp-feature-section ${isReversed ? 'pp-feature-alt' : ''}`}>
            <div className="container">
              <div className={`pp-feature-row ${isReversed ? 'pp-feature-row-reverse' : ''}`}>
                <div className="pp-feature-text">
                  <div className="pp-feature-icon-badge" style={{ background: s.bg, color: s.color }}>
                    <SIcon size={20} />
                    {s.IconExtra && <s.IconExtra size={14} />}
                  </div>
                  <h2 className="pp-feature-title">{s.title}</h2>
                  {s.paragraphs.map((p, j) => (
                    <p key={j} className="pp-feature-paragraph">{p}</p>
                  ))}
                  <div className="pp-feature-bullets">
                    {s.bullets.map((b) => (
                      <div key={b} className="pp-feature-bullet">
                        <CheckCircle size={18} color="var(--green)" style={{ flexShrink: 0, marginTop: 2 }} />
                        <span>{b}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="pp-feature-visual">
                  <div className="pp-feature-mockup">
                    <div className="pp-mockup-bar">
                      <div className="pp-mockup-dots">
                        <span /><span /><span />
                      </div>
                      <div className="pp-mockup-url">leadnova.co</div>
                    </div>
                    <div className="pp-mockup-content" style={{ background: s.bg }}>
                      <SIcon size={48} color={s.color} strokeWidth={1.5} />
                      <div className="pp-mockup-label" style={{ color: s.color }}>{s.title}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      <section className="pp-cta-section">
        <div className="pp-cta-glow" />
        <div className="container pp-cta-inner">
          <h2 className="pp-cta-title">Ready to see it in action?</h2>
          <p className="pp-cta-desc">
            Start your 14-day free trial today. No credit card required.
          </p>
          <div className="pp-cta-buttons">
            {!isAuthenticated && (
              <Link to="/pricing" className="pp-btn-primary">
                Start Free Trial <ArrowRight size={18} />
              </Link>
            )}
            <Link to="/pricing" className="pp-btn-secondary-light">View Pricing</Link>
          </div>
        </div>
      </section>

      <style>{`
        .pp { overflow-x: hidden; }

        .pp-hero {
          background: var(--navy); position: relative;
          padding: 140px 0 80px; text-align: center; overflow: hidden;
        }
        .pp-hero-bg {
          position: absolute; inset: 0;
          background: radial-gradient(ellipse 80% 60% at 50% 0%, rgba(77,184,232,0.18), transparent 70%);
        }
        .pp-hero-dots {
          position: absolute; inset: 0; opacity: 0.03;
          background-image: radial-gradient(circle, #fff 1px, transparent 1px);
          background-size: 32px 32px;
        }
        .pp-hero-inner { position: relative; z-index: 1; }
        .pp-hero-pill {
          display: inline-flex; align-items: center; gap: 8px;
          background: rgba(77,184,232,0.12); border: 1px solid rgba(77,184,232,0.25);
          border-radius: 100px; padding: 6px 18px; margin-bottom: 24px;
          font-size: 13px; font-weight: 600; color: var(--sky);
        }
        .pp-hero-title {
          font-size: 52px; font-weight: 800; color: #fff;
          line-height: 1.1; max-width: 700px; margin: 0 auto 20px;
          letter-spacing: -0.03em;
        }
        .pp-hero-desc {
          font-size: 19px; color: rgba(255,255,255,0.6);
          max-width: 560px; margin: 0 auto 36px; line-height: 1.7;
        }
        .pp-hero-cta { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; }

        .pp-btn-primary {
          display: inline-flex; align-items: center; gap: 8px;
          background: var(--green); color: #fff;
          font-size: 16px; font-weight: 600; padding: 14px 32px;
          border-radius: 10px; text-decoration: none;
          box-shadow: 0 4px 14px rgba(58,170,53,0.4);
          transition: all 0.2s;
        }
        .pp-btn-primary:hover { text-decoration: none; opacity: 0.9; }
        .pp-btn-secondary {
          display: inline-flex; align-items: center; gap: 8px;
          background: rgba(255,255,255,0.1); color: rgba(255,255,255,0.9);
          font-size: 16px; font-weight: 500; padding: 14px 32px;
          border-radius: 10px; text-decoration: none;
          border: 1px solid rgba(255,255,255,0.2); transition: all 0.2s;
        }
        .pp-btn-secondary:hover { text-decoration: none; background: rgba(255,255,255,0.15); }
        .pp-btn-secondary-light {
          display: inline-flex; align-items: center; gap: 8px;
          background: rgba(255,255,255,0.1); color: rgba(255,255,255,0.9);
          font-size: 16px; font-weight: 500; padding: 14px 32px;
          border-radius: 10px; text-decoration: none;
          border: 1px solid rgba(255,255,255,0.2); transition: all 0.2s;
        }
        .pp-btn-secondary-light:hover { text-decoration: none; background: rgba(255,255,255,0.15); }

        .pp-highlights-section { padding: 80px 0; background: #fff; }
        .pp-highlights-grid {
          display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px;
        }
        .pp-highlight-card {
          text-align: center; padding: 32px 24px;
          border-radius: 16px; border: 1px solid var(--gray-100);
          background: var(--gray-50);
          transition: all 0.2s;
        }
        .pp-highlight-card:hover {
          border-color: var(--sky); box-shadow: 0 8px 24px rgba(77,184,232,0.08);
          transform: translateY(-2px);
        }
        .pp-highlight-icon {
          width: 48px; height: 48px; border-radius: 12px;
          background: var(--sky-light); color: var(--sky);
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 16px;
        }
        .pp-highlight-title { font-size: 16px; font-weight: 700; color: var(--navy); margin-bottom: 8px; }
        .pp-highlight-desc { font-size: 14px; color: var(--gray-500); line-height: 1.6; margin: 0; }

        .pp-feature-section { padding: 80px 0; background: #fff; }
        .pp-feature-alt { background: var(--gray-50); }
        .pp-feature-row {
          display: grid; grid-template-columns: 1fr 1fr;
          gap: 60px; align-items: center;
        }
        .pp-feature-row-reverse .pp-feature-text { order: 2; }
        .pp-feature-row-reverse .pp-feature-visual { order: 1; }

        .pp-feature-icon-badge {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 10px 16px; border-radius: 12px;
          margin-bottom: 16px; font-weight: 600;
        }
        .pp-feature-title {
          font-size: 30px; font-weight: 800; color: var(--navy);
          margin-bottom: 20px; letter-spacing: -0.02em; line-height: 1.2;
        }
        .pp-feature-paragraph {
          font-size: 16px; color: var(--gray-500);
          line-height: 1.75; margin-bottom: 16px;
        }
        .pp-feature-bullets { margin-top: 24px; }
        .pp-feature-bullet {
          display: flex; align-items: flex-start; gap: 10px;
          margin-bottom: 12px; font-size: 15px; color: var(--gray-900);
        }

        .pp-feature-visual { display: flex; justify-content: center; }
        .pp-feature-mockup {
          width: 100%; max-width: 480px;
          border-radius: 16px; overflow: hidden;
          box-shadow: 0 12px 48px rgba(0,0,0,0.1), 0 2px 8px rgba(0,0,0,0.06);
          border: 1px solid var(--gray-200); background: #fff;
        }
        .pp-mockup-bar {
          display: flex; align-items: center; gap: 12px;
          padding: 12px 16px; background: var(--gray-50);
          border-bottom: 1px solid var(--gray-100);
        }
        .pp-mockup-dots { display: flex; gap: 6px; }
        .pp-mockup-dots span {
          width: 10px; height: 10px; border-radius: 50%;
          background: var(--gray-200);
        }
        .pp-mockup-dots span:first-child { background: #FF5F57; }
        .pp-mockup-dots span:nth-child(2) { background: #FFBD2E; }
        .pp-mockup-dots span:nth-child(3) { background: #28C840; }
        .pp-mockup-url {
          flex: 1; text-align: center;
          font-size: 12px; color: var(--gray-500);
          background: #fff; padding: 4px 12px; border-radius: 6px;
          border: 1px solid var(--gray-100);
        }
        .pp-mockup-content {
          padding: 60px 40px; text-align: center;
          display: flex; flex-direction: column; align-items: center;
          gap: 16px; min-height: 220px; justify-content: center;
        }
        .pp-mockup-label {
          font-size: 16px; font-weight: 700; letter-spacing: -0.01em;
        }

        .pp-cta-section {
          background: var(--navy); padding: 80px 0;
          text-align: center; position: relative; overflow: hidden;
        }
        .pp-cta-glow {
          position: absolute; inset: 0;
          background: radial-gradient(ellipse 60% 50% at 50% 50%, rgba(77,184,232,0.12), transparent);
        }
        .pp-cta-inner { position: relative; z-index: 1; }
        .pp-cta-title {
          font-size: 38px; font-weight: 800; color: #fff;
          margin-bottom: 16px; letter-spacing: -0.02em;
        }
        .pp-cta-desc {
          font-size: 18px; color: rgba(255,255,255,0.6);
          max-width: 500px; margin: 0 auto 32px;
        }
        .pp-cta-buttons { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; }

        /* ========== TABLET (768px) ========== */
        @media (max-width: 768px) {
          .pp-hero { padding: 120px 0 60px; }
          .pp-hero-title { font-size: 36px; }
          .pp-hero-desc { font-size: 16px; margin: 0 auto 28px; }

          .pp-highlights-grid { grid-template-columns: 1fr 1fr; gap: 16px; }
          .pp-highlight-card { padding: 24px 18px; }

          .pp-feature-section { padding: 56px 0; }
          .pp-feature-row {
            grid-template-columns: 1fr; gap: 32px;
          }
          .pp-feature-row-reverse .pp-feature-text { order: 1; }
          .pp-feature-row-reverse .pp-feature-visual { order: 2; }
          .pp-feature-title { font-size: 26px; }

          .pp-cta-title { font-size: 28px; }
          .pp-cta-desc { font-size: 16px; }
        }

        /* ========== SMALL MOBILE (480px) ========== */
        @media (max-width: 480px) {
          .pp-hero { padding: 110px 0 48px; }
          .pp-hero-title { font-size: 28px; }
          .pp-hero-desc { font-size: 15px; }
          .pp-hero-cta { flex-direction: column; align-items: stretch; }
          .pp-btn-primary, .pp-btn-secondary, .pp-btn-secondary-light {
            justify-content: center; padding: 12px 24px; font-size: 15px;
          }

          .pp-highlights-section { padding: 48px 0; }
          .pp-highlights-grid { grid-template-columns: 1fr; gap: 12px; }
          .pp-highlight-card { padding: 20px 16px; }

          .pp-feature-section { padding: 40px 0; }
          .pp-feature-row { gap: 24px; }
          .pp-feature-title { font-size: 22px; }
          .pp-feature-paragraph { font-size: 15px; }
          .pp-feature-bullet { font-size: 14px; }

          .pp-mockup-content { padding: 40px 24px; min-height: 180px; }

          .pp-cta-section { padding: 56px 0; }
          .pp-cta-title { font-size: 24px; }
          .pp-cta-desc { font-size: 15px; }
          .pp-cta-buttons { flex-direction: column; align-items: stretch; }
        }
      `}</style>
    </div>
  );
}
